/**
 * ChargesPage — Cobrança — Títulos. 1 linha = 1 charge (operação + charge_id).
 *
 * Substitui a antiga tela de Títulos de Dívida (que era do settlement, 1 linha = 1 title_debt).
 * Aqui a linha vem de billing.vw_charges_list, que agrega as faturas daquele charge e traz de
 * fora o estado da dívida (settlement) e da operação (credit).
 *
 * A coluna Status mostra o estado RESOLVIDO (com PENDING derivado: devendo e sem fatura aberta),
 * e o tooltip abre os dois de origem — dívida (title_debt) e cobrança (cadeia de faturas).
 * Divergir entre eles é NORMAL em parcela: paga-se a garantia e a fatura da parcela fica intocada.
 * Ficavam em coluna própria antes; como é informação de exceção, virou tooltip.
 *
 * Movimentações NÃO ficam aqui (são de title_debt/title_movement): elas vivem no menu de ações
 * da tela de Operações de Crédito.
 */
import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import { formatTaxId } from '../../../utils/taxIdUtils';
import UnidentifiedPaymentsModal, { ApplyMode } from './modals/UnidentifiedPaymentsModal';
import { addFilter } from './constants/chargeFilterFromPayment';
import appConfig from '../../../config/app-config';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter } from '../../molecules/FilterBuilder/types';
import {
    chargeFilterSchema, serializeFilters,
    CHARGE_SCENARIO_LABELS, CHARGE_STATUS_LABELS,
} from './constants/chargeFilterSchema';
import ChargeInvoicesModal from './modals/ChargeInvoicesModal';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatDateBR } from '../../../utils/DateUtils';
import { FaFileInvoiceDollar, FaExchangeAlt, FaSearchDollar } from 'react-icons/fa';
import MovementsModal from '../settlement/modals/MovementsModal';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise';

/**
 * A linha da grade é camelCase (billing) e o MovementsModal — que é do settlement e é reusado
 * aqui inteiro — lê snake_case. O adaptador vive aqui, e não dentro do modal: quem chega depois é
 * que se ajusta, senão o modal passa a servir dois dialetos e alguém troca um pelo outro.
 */
export const asTitleDebt = (row: any) => ({
    title_debt_id: row.titleDebtId,
    contract_number: row.contractNumber || row.contractLabel,
    // o rótulo já vem pronto do backend (contrato-aditamento); o modal só monta quando falta
    contract_label: row.contractLabel,
    installment_number: row.installmentNumber,
    document_title: row.documentTitle,
    amount: row.amount,
    paid_amount: row.paidAmount ?? 0,
    due_date: row.dueDate,
    status: row.titleDebtStatus || row.chargeStatus,
    contract_modality: row.contractModality,
    // natureza do título: o cenário do charge É a business_reference dele
    business_reference: row.chargeScenario,
    /**
     * O selo pronto, idêntico ao da LINHA de onde o modal foi aberto. Esta tela resolve o status
     * combinando dívida e cobrança (e tem o "Paga pós Liq."), coisa que o modal não sabe fazer —
     * mandava-se `titleDebtStatus` cru e a linha dizia "Paga pós Liq." enquanto o modal dizia
     * "PAID", dois estados para o mesmo título.
     */
    ...chargeStatusBadge(row),
});

/**
 * O selo PRINCIPAL da coluna Status — o estado da dívida.
 *
 * `PENDING` não entra aqui: "falta faturar" é uma pendência NOSSA, não um estado do título.
 * Mostrá-lo no lugar do status escondia se a dívida está em aberto ou parcialmente paga, que é o
 * que o usuário precisa saber para decidir o valor da fatura que vai gerar. Ele vira selo à parte
 * (ver a célula lá embaixo), ao lado do estado real.
 */
const chargeStatusBadge = (row: any) => {
    if (row.hasOverpayment) return { status_label: 'Paga pós Liq.', status_color: 'teal' };
    const status = isPending(row)
        ? (row.titleDebtStatus || row.invoicesStatus || 'OPEN')
        : row.chargeStatus;
    return {
        status_label: CHARGE_STATUS_LABELS[status] || status,
        status_color: statusColors[status] || 'gray',
    };
};

/** Falta cobrança para o que ainda se deve — quem decide é a view (ver charge_status). */
const isPending = (row: any) => row.chargeStatus === 'PENDING';

// Paleta de cobrança — ver o comentário em pages/settlement/constants/settlement.ts:
// todo pagamento é verde, o tom diz qual; o que não é pagamento sai da família.
const statusColors: Record<string, BadgeColor> = {
    OPEN: 'gold',
    PARTIALLY_PAID: 'olive',    // pagamento parcial
    PAID: 'green',              // liquidado
    OVERPAID: 'teal',           // recebimento a maior
    PENDING: 'todo',            // falta gerar fatura — pendência nossa, não do cliente
    CANCELLED: 'red',
    SUPERSEDED: 'gray',
    PAID_WITH_LOSS: 'olive',
};

/**
 * Houve cobertura a mais neste charge?
 *
 * ⚠️ **Só a fração responde isso, nunca o valor.** Boleto com multa paga acima do amount e continua
 * sendo 1/1 — é mora, e o motor aplica o valor cheio de propósito (mesma regra que cobre desconto).
 * O excesso de verdade é quando as frações PAGAS somam mais de 1: duas faturas cobrindo o mesmo
 * pedaço da dívida. A view arredonda `paid_fraction` em 6 casas, então fechamento exato cai em
 * 1.000000 e não dispara.
 */
/**
 * Cobrança redundante que a AUTOMAÇÃO fechou. A conta é da view (`boleto_over_coverage`): fração
 * paga acima de 1 E o boleto tendo sido quem liquidou o charge.
 *
 * Não é `paidFraction > 1` sozinho, que era o critério antigo. Pela tela o usuário edita o valor
 * e sabe o que está fazendo — pagar 513.537,80 e depois 42.419,00 num título de 555.956,80 é o
 * valor exato em duas vezes, e aparecia como excesso. Quem decide sem ninguém olhando é o
 * BOLETO_PAID, que lê a fração para responder se o pagamento quita o título; só ali a cobertura
 * redundante vira decisão errada.
 */
const isOverCovered = (row: any) => !!row.boletoOverCoverage;

/** O valor é ILUSTRATIVO: o que sobra em dinheiro não coincide com o pedaço coberto duas vezes. */
const overCoverageTooltip = (row: any) => {
    const base = 'Um boleto liquidou este charge com as faturas pagas já somando '
        + `${row.paidFraction} — mais de uma cobriu o mesmo pedaço`;
    return row.remainingAmount < 0
        ? `${base}. Recebido ${formatBRL(Math.abs(row.remainingAmount))} acima do devido (valor aproximado)`
        : base;
};

// o ATable substitui a classe do cabeçalho inteira quando cssClass é informado, então a borda
// e a largura precisam vir junto — daí a constante em vez de só 'text-right'
const DIREITA = 'border-b text-right w-fit';

const scenarioColors: Record<string, BadgeColor> = {
    RECEIVABLE: 'indigo',
    INSTALLMENT: 'blue',
    GUARANTEE: 'gray',
};

/**
 * Filtros vindos da URL — deep-link de outra tela ("Listagem de Títulos" nas Operações de Crédito
 * manda `credit_operation_id`). Entram já aplicados: chegar aqui com a tela inteira e ter de
 * filtrar à mão anularia o atalho.
 */
const DEEP_LINK_KEYS = [
    { param: 'credit_operation_id', label: 'Operação (id)' },
    { param: 'charge_id', label: 'Charge (id)' },
] as const;

const ChargesPage = () => {
    const [searchParams] = useSearchParams();
    const deepLinkFilters = useMemo<AppliedFilter[]>(() => (
        DEEP_LINK_KEYS.flatMap(({ param, label }) => {
            const value = searchParams.get(param);
            return value
                ? [{ id: `deep_${param}`, key: param, label, type: 'text' as const, operator: 'eq' as const, value }]
                : [];
        })
        // searchParams é recriado a cada render; a chave estável é a query string
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [searchParams.toString()]);

    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    // vazio de propósito: sem `sort` o backend aplica a ordem de leitura da tela (contrato →
    // natureza → vencimento → valor), que não cabe num único campo de coluna
    const [currentSortField, setCurrentSortField] = useState('');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    // semeado com o deep-link: a primeira busca já sai filtrada, sem ida e volta
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>(deepLinkFilters);
    const [refreshKey, setRefreshKey] = useState(0);
    const [invoicesCharge, setInvoicesCharge] = useState<any>(null);
    const [movementsCharge, setMovementsCharge] = useState<any>(null);
    const [showPayments, setShowPayments] = useState(false);
    /**
     * Filtros semeados de fora depois da montagem (o atalho da lista de pagamentos).
     *
     * `null` = ninguém injetou nada, e o deep-link da URL continua governando. Assim que o usuário
     * usa um atalho, a intenção DELE passa a valer — reaplicar o deep-link em cima desfaria o
     * clique que ele acabou de dar.
     *
     * A troca de referência é o que faz o FilterBuilder se re-semear: ele observa `initialFilters`.
     */
    const [injectedFilters, setInjectedFilters] = useState<AppliedFilter[] | null>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

    const sortParam = !currentSortField ? ''
        : currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
        });
        if (sortParam) p.set('sort', sortParam);
        serializeFilters(activeFilters).forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    // '+' não é decodificado pelo API Gateway — espaço tem que ir como %20.
    // refreshKey força o useFetch a refazer a busca depois de uma ação (a URL precisa mudar).
    const url = `${API_URL}/charges?${params.toString().replace(/\+/g, '%20')}&_r=${refreshKey}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    /**
     * Aplica o filtro vindo da lista de pagamentos.
     *
     * Mexe nos DOIS estados de propósito: `activeFilters` é o que vai para a busca, e
     * `injectedFilters` é o que o painel mostra. Atualizar só o primeiro filtraria a tela sem o
     * usuário ver por quê — e ele não teria como desfazer o que não aparece.
     */
    const applyPaymentFilter = useCallback(
        (filter: AppliedFilter, mode: ApplyMode) => {
            setActiveFilters(atuais => {
                const upcoming = mode === 'ONLY' ? [filter] : addFilter(atuais, filter);
                setInjectedFilters(upcoming);
                return upcoming;
            });
            setCurrentPage(0);
        }, []);

    const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], [API_KEY]);

    const tableHeaders = useMemo(() => [
        // o default do ATable já é text-center; declaramos só quem foge dele
        { id: 'contract_label', title: 'Contrato', canSort: true, field: 'contract_label', cssClass: DIREITA },
        // Quem EMITIU o título (cedente), que não é quem paga: em cessão são empresas diferentes.
        // Fica ao lado do contrato porque as duas identificam a ORIGEM da dívida.
        { id: 'title_payer', title: 'Pagador', canSort: false, width: '1%' },
        { id: 'charge_scenario', title: 'Natureza', canSort: false },
        // Ordem padrão das telas de cobrança: Natureza, Título/NF, Nº — o documento identifica a
        // dívida, o número a posiciona dentro dela.
        { id: 'document_title', title: 'Título/NF', canSort: false },
        // nº do título: fraciona a OPERAÇÃO ("3 de 16"), diferente da coluna Fração do drill-in,
        // que fraciona o CHARGE
        { id: 'installment_number', title: 'Nº', canSort: true, field: 'installment_number' },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount', cssClass: DIREITA },
        { id: 'paid_amount', title: 'Pago', canSort: true, field: 'paid_amount', cssClass: DIREITA },
        { id: 'remaining_amount', title: 'Falta', canSort: true, field: 'remaining_amount', cssClass: DIREITA },
        { id: 'due_date', title: 'Vencimento', canSort: true, field: 'due_date' },
        { id: 'charge_status', title: 'Status', canSort: true, field: 'charge_status' },
        { id: 'invoices', title: 'Faturas', canSort: false },
    ], []);

    const renderRows = (rows: any[]) => rows.map((row: any) => (
        <tr key={`${row.creditOperationId}-${row.chargeId}`}
            className="hover:bg-gray-50 dark:hover:bg-slate-700">
            <td className="px-4 py-2 text-sm font-medium text-right" title={row.creditOperationId}>
                {row.contractLabel || row.contractNumber || '-'}
            </td>
            <td className="px-4 py-2 text-sm text-left">
                {row.titlePayer?.name ? (
                    <div>
                        {/* truncate com o nome no title: razão social de agro é longa e empurraria
                            as colunas de valor para fora da tela */}
                        <div className="font-medium truncate max-w-[180px]" title={row.titlePayer.name}>
                            {row.titlePayer.name}
                        </div>
                        <div className="text-xs text-gray-500">
                            {formatTaxId(row.titlePayer.documentNumber)}
                        </div>
                    </div>
                ) : '-'}
            </td>
            <td className="px-4 py-2 text-sm text-center">
                <BadgeStatus
                    text={CHARGE_SCENARIO_LABELS[row.chargeScenario] || row.chargeScenario}
                    color={scenarioColors[row.chargeScenario] || 'gray'}
                />
            </td>
            <td className="px-4 py-2 text-sm text-center">{row.documentTitle || '-'}</td>
            <td className="px-4 py-2 text-sm text-center">{row.installmentNumber ?? '-'}</td>
            <td className="px-4 py-2 text-sm text-right">
                {row.amount != null ? formatBRL(row.amount) : '-'}
            </td>
            <td className={`px-4 py-2 text-sm text-right ${row.paidAmount > 0 ? 'text-green-600 font-medium' : ''}`}>
                {row.paidAmount != null ? formatBRL(row.paidAmount) : '-'}
            </td>
            {/* ⚠️ Quem diz que houve excesso é a FRAÇÃO, nunca o valor: boleto com multa paga acima
                do amount e continua sendo 1/1 — é mora, não excedente. O valor negativo em Falta é
                apenas o retrato disso, e é simbólico: o que sobra em dinheiro dificilmente coincide
                com o pedaço coberto duas vezes.

                Aqui o destaque é só o peso da fonte, sem cor. A cor que identifica cobertura
                redundante é o darkturquoise, e ele só funciona como FUNDO: como texto sobre branco
                não tem contraste para ler, e escurecê-lo até dar contraste devolve o teal do
                recebimento a maior — exatamente a confusão que a cor nova veio desfazer. Quem
                carrega o tom é o selo ao lado, que tem fundo. */}
            <td className={`px-4 py-2 text-sm text-right ${isOverCovered(row) ? 'font-semibold' : ''}`}
                title={isOverCovered(row) ? overCoverageTooltip(row) : undefined}>
                {row.remainingAmount != null ? formatBRL(row.remainingAmount) : '-'}
            </td>
            <td className={`px-4 py-2 text-sm text-center ${row.isOverdue ? 'text-red-600 font-medium' : ''}`}>
                {row.dueDate ? formatDateBR(row.dueDate) : '-'}
            </td>
            {/* o tooltip abre a origem do status: dívida (settlement) e cobrança (cadeia de
                faturas). Divergir é normal em parcela paga via garantia. */}
            <td className="px-4 py-2 text-sm whitespace-nowrap text-center"
                // cobrança nula = título sem NENHUMA fatura (a view devolve null, não 'CANCELLED'):
                // é o caso "falta faturar", e dizer "—" esconderia a diferença
                title={`Dívida: ${row.titleDebtStatus || '—'} · Cobrança: ${row.invoicesStatus || 'sem fatura'}`}>
                <div className="inline-flex flex-wrap items-center justify-center gap-1">
                    {/* pagamento que chegou depois de o título já estar liquidado: um selo só, porque
                        "Pago" + "EXCEDENTE" lado a lado lia como dois estados diferentes.
                        O mesmo selo vai para o modal de movimentações, via asTitleDebt. */}
                    <BadgeStatus text={chargeStatusBadge(row).status_label}
                        color={chargeStatusBadge(row).status_color as BadgeColor} />
                    {/* ao LADO do estado da dívida, não no lugar dele: são coisas diferentes —
                        "Parcial" diz quanto se deve, "Falta faturar" diz que ninguém está cobrando */}
                    {isPending(row) && (
                        <span title="Parte do que se deve não tem fatura cobrando">
                            <BadgeStatus text="Falta faturar" color="todo" />
                        </span>
                    )}
                    {/* Passo 2 do ato de pagar: já tem fatura com pagamento sinalizado esperando
                        aprovação. Ao LADO do estado da dívida porque não o substitui — o título
                        continua devendo até o financeiro aprovar. Sem este selo, quem abre a tela
                        vê um título "Em aberto" e vai cobrar de quem já pagou. */}
                    {row.hasSignalledPayment && (
                        <span title="Há fatura com pagamento sinalizado neste título, aguardando aprovação do financeiro">
                            <BadgeStatus text="Pagto Sinalizado" color="yellow" />
                        </span>
                    )}
                    {row.isOverdue && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded font-medium">
                            VENCIDO
                        </span>
                    )}
                    {/* distinto de "Paga pós Liq.": lá o excedente foi para o coletor OVERPAID; aqui ele
                        está DENTRO do título, porque na hora do pagamento ele ainda estava aberto */}
                    {isOverCovered(row) && (
                        <span title={overCoverageTooltip(row)}>
                            <BadgeStatus text="Coberto a mais" color="turquoise" />
                        </span>
                    )}
                </div>
            </td>
            <td className="px-4 py-2 text-sm whitespace-nowrap text-center">
                <button
                    className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 inline-flex items-center gap-1"
                    onClick={() => setInvoicesCharge(row)}
                    title="Ver e agir nas faturas deste charge"
                ><FaFileInvoiceDollar /> {row.invoicesCount ?? 0}
                    {row.openInvoicesCount > 0 && (
                        <span className="text-[10px] text-amber-600 font-semibold">
                            ({row.openInvoicesCount} aberta{row.openInvoicesCount > 1 ? 's' : ''})
                        </span>
                    )}
                </button>
                {row.titleDebtId && (
                    <button
                        className="ml-1 px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 inline-flex items-center gap-1"
                        onClick={() => setMovementsCharge(row)}
                        title="Movimentações do título — inclui as que vieram do pagamento de uma garantia"
                    ><FaExchangeAlt /></button>
                )}
            </td>
        </tr>
    ));

    return (
        <>
            <PageList
                pageTitle="Cobrança — Títulos"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Cobrança', url: '#' },
                    { label: 'Cobrança — Títulos', url: '/billing/charges' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => {
                    setCurrentSortField(field);
                    setCurrentSortOrder(order);
                }}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="títulos"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={chargeFilterSchema}
                            onSearch={handleFilterSearch}
                            collapsible
                            defaultCollapsed
                            title="Filtros"
                            /* injetado vence o deep-link: o atalho é ação do usuário AGORA, e
                               voltar ao da URL desfaria o clique que ele acabou de dar */
                            initialFilters={injectedFilters ?? deepLinkFilters}
                        />
                    ),
                    controlSlot: (
                        <button
                            onClick={() => setShowPayments(true)}
                            title="Pagamentos Não Identificados"
                            aria-label="Pagamentos Não Identificados"
                            className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                        >
                            <FaSearchDollar size={18} />
                        </button>
                    ),
                }}
            </PageList>
            {showPayments && (
                <UnidentifiedPaymentsModal
                    onClose={() => setShowPayments(false)}
                    onApplyFilter={applyPaymentFilter}
                />
            )}
            {invoicesCharge && (
                <ChargeInvoicesModal
                    charge={invoicesCharge}
                    onClose={() => setInvoicesCharge(null)}
                    onChanged={() => setRefreshKey(k => k + 1)}
                />
            )}
            {movementsCharge && (
                <MovementsModal
                    titleDebt={asTitleDebt(movementsCharge)}
                    onClose={() => setMovementsCharge(null)}
                />
            )}
        </>
    );
};

export default ChargesPage;
